import type { PreviewLocalization } from "@intelligems/ig-clients/storefront-navigation/preview-localization";
import { createSelector, lruMemoize } from "@reduxjs/toolkit";

import type { RootState } from "@src/store/rootState";
import type { PreviewLocalizationField } from "@src/store/slices/experienceBuilder/common/selectors/getPreviewLocalization";
import { ExperienceBuilderSelector } from "@src/store/slices/experienceBuilder";
import { getOrganization } from "@src/store/slices/organization";

/**
 * What the Navigator does when a tokenless request answers that the Online
 * Store channel is locked: the store has a storefront password.
 */
export type LockedChannelFallback =
  /** The own store: query again with the org's public token. */
  | { kind: "storefrontToken"; token: string }
  /** The own store's org has no `storefrontToken`: it needs the backfill. */
  | { kind: "missingStorefrontToken"; organizationId: string | undefined }
  /** A demo URL or a shop not verified as the org's: never use the org's token. */
  | { kind: "none" };

/**
 * The store and Preview localization of every Navigator query. Every query is
 * tokenless first; `lockedChannelFallback` applies only after a lock.
 */
export interface ReadyStorefrontNavigationTarget {
  status: "ready";
  shop: string;
  lockedChannelFallback: LockedChannelFallback;
  localization: PreviewLocalization;
}

/** An `ig:shopify` field that every Navigator query needs. */
export type PreviewReportField = PreviewLocalizationField | "shop";

/**
 * What the Navigator can read. Only a `ready` target sends queries: the
 * Navigator sends no query without the Preview localization.
 */
export type StorefrontNavigationTarget =
  /** The preview has not sent `ig:shopify` yet. */
  | { status: "waitingForPreview" }
  /**
   * The preview sent `ig:shopify` without a field that the queries need,
   * for example from a reverse proxy build that does not report
   * `routes.root`.
   */
  | { status: "previewReportIncomplete"; missing: PreviewReportField[] }
  | { status: "unknownLocale"; locale: string }
  | { status: "unknownCountry"; country: string }
  | ReadyStorefrontNavigationTarget;

const sameFallback = (a: LockedChannelFallback, b: LockedChannelFallback) => {
  if (a.kind === "storefrontToken" && b.kind === "storefrontToken") {
    return a.token === b.token;
  }
  if (
    a.kind === "missingStorefrontToken" &&
    b.kind === "missingStorefrontToken"
  ) {
    return a.organizationId === b.organizationId;
  }
  return a.kind === b.kind;
};

const sameTarget = (
  a: StorefrontNavigationTarget,
  b: StorefrontNavigationTarget
): boolean => {
  if (a.status === "ready" && b.status === "ready") {
    return (
      a.shop === b.shop &&
      sameFallback(a.lockedChannelFallback, b.lockedChannelFallback) &&
      a.localization.country === b.localization.country &&
      a.localization.language === b.localization.language &&
      a.localization.root === b.localization.root
    );
  }
  if (
    a.status === "previewReportIncomplete" &&
    b.status === "previewReportIncomplete"
  ) {
    return (
      a.missing.length === b.missing.length &&
      a.missing.every((field, index) => field === b.missing[index])
    );
  }
  if (a.status === "unknownLocale" && b.status === "unknownLocale") {
    return a.locale === b.locale;
  }
  if (a.status === "unknownCountry" && b.status === "unknownCountry") {
    return a.country === b.country;
  }
  return a.status === b.status;
};

/**
 * Resolves the Storefront target from the preview and the org. The domain is
 * the myshopify domain that the preview reports in `ig:shopify`, for the own
 * store and for a demo URL. Every store is read tokenless first: that reads
 * the Online Store channel, which the Embedded preview shows. Only the own
 * store falls back to the org's public `storefrontToken`, when its channel is
 * locked by a storefront password (ADR 0017).
 *
 * The preview sends `ig:shopify` on every page load. An equal target keeps
 * its identity, so the Navigator runs a query only when the target changes.
 */
export const getStorefrontNavigationTarget = createSelector(
  [
    ExperienceBuilderSelector.getPreviewLocalization,
    ExperienceBuilderSelector.getShopifyContext,
    ExperienceBuilderSelector.isAuthenticated,
    (state: RootState) => getOrganization(state)?.storefrontToken,
    (state: RootState) => getOrganization(state)?.id,
    (state: RootState) => getOrganization(state)?.shopId,
  ],
  (
    previewLocalization,
    shopifyContext,
    isAuthenticated,
    storefrontToken,
    organizationId,
    organizationShop
  ): StorefrontNavigationTarget => {
    const shop = shopifyContext?.shop;
    switch (previewLocalization.status) {
      case "unavailable":
        return { status: "waitingForPreview" };
      case "incomplete":
        return {
          status: "previewReportIncomplete",
          missing: shop
            ? previewLocalization.missing
            : [...previewLocalization.missing, "shop"],
        };
      case "unknownLocale":
        return { status: "unknownLocale", locale: previewLocalization.locale };
      case "unknownCountry":
        return {
          status: "unknownCountry",
          country: previewLocalization.country,
        };
      case "available":
        break;
    }

    if (!shop) {
      return { status: "previewReportIncomplete", missing: ["shop"] };
    }

    // Leaving demo mode changes isAuthenticated before the new preview reports
    // its shop. The org's credential belongs only to its canonical shopId,
    // never to the previous demo shop still present in the preview context.
    const isOwnStore =
      isAuthenticated && shop.toLowerCase() === organizationShop?.toLowerCase();
    const lockedChannelFallback: LockedChannelFallback = !isOwnStore
      ? { kind: "none" }
      : storefrontToken
        ? { kind: "storefrontToken", token: storefrontToken }
        : { kind: "missingStorefrontToken", organizationId };
    return {
      status: "ready",
      shop,
      lockedChannelFallback,
      localization: previewLocalization.localization,
    };
  },
  // lruMemoize of size 1 compares every new result with the last returned
  // one. The default weakMapMemoize also returns cached results for older
  // inputs, and then an equal target can get a new identity.
  { memoize: lruMemoize, memoizeOptions: { resultEqualityCheck: sameTarget } }
);
